import { DEFAULT_CAPACITY, DEFAULT_STEPS } from './formingBarBuffer'
import type { SubBarSource } from '../adapters/subBarSocket'

/**
 * How finely a forming bar is drawn, and how much of it is held in memory.
 *
 * All of it turns on ONE question: how long does this bar dwell in WALL-CLOCK time? Ordinary
 * replay spends 250-1000ms on a bar, so 32 snapshots is a state every ~31ms — smooth, and the
 * numbers `formingBarBuffer` was written against. Real-time spends a whole timeframe on it, and
 * 32 snapshots across an hour is one state every 112 SECONDS: a candle that changes twice in
 * four minutes, which reads as broken rather than as building.
 *
 * So the geometry follows the dwell. Kept out of the hook because it is pure arithmetic with
 * sharp edges — memory ceilings, data resolution, a source that may or may not be able to
 * subdivide the bar — and none of that needs a React render to test.
 */

/** Bars folded ahead of the cursor on the M1 path. Matches the ring capacity. */
export const LOOKAHEAD_BARS = 16
/**
 * Bars folded ahead on the raw-tick path. Far fewer, because a bar holds hundreds to thousands
 * of ticks rather than a handful of M1 rows: 4 bars of M5 ticks is already ~15k rows in the
 * busiest sessions.
 */
export const TICK_LOOKAHEAD_BARS = 4

/** Wall-clock ms one distinct candle state should occupy at real-time pacing. */
export const TARGET_STEP_MS = 100
/** Never coarser than ordinary replay's own default. */
export const MIN_REALTIME_STEPS = DEFAULT_STEPS
/**
 * Memory ceiling, in snapshots per bar.
 *
 * A cell is 28 bytes (3x Float64 + 1x Float32), so the worst case here is
 * `REALTIME_CAPACITY x MAX_REALTIME_STEPS x 28` = 672 KiB — half the ~1.3 MB tick payload a
 * dense H1 bar puts on the wire, allocated once and freed when the detail changes. Halving it
 * costs resolution at H1 and nothing anywhere else.
 */
export const MAX_REALTIME_STEPS = 8192
/** The forming bar, one bar of runway, and a slot of slack so the ring never evicts either. */
export const REALTIME_CAPACITY = 3
/**
 * Two bars of runway, and `refillBelow` matches it.
 *
 * With one bar the refill test (`ready >= refillBelow`) is satisfied by the forming bar itself,
 * so nothing is ever fetched ahead: every bar boundary would blank the candle while a ~700 KB
 * round trip runs. Asking for two means exactly one request per bar, issued a whole timeframe
 * before it is needed.
 */
export const REALTIME_LOOKAHEAD_BARS = 2
/**
 * Bars per request in real-time. One, so a fill stays ~700 KB at H1.
 *
 * Distinct from the lookahead: in the steady state only one bar is ever unfolded, but the first
 * fill after a scrub or a detail change has both, and would otherwise ask for two hours of
 * ticks in one frame.
 */
export const REALTIME_MAX_BARS_PER_FILL = 1

/** Row length of the 'm1' path — an M1 bar is the finest thing it can offer. */
const SUB_BAR_SECONDS = 60

export interface FormingBarPlan {
  source: SubBarSource
  /** Bars the ring holds. */
  capacity: number
  /** Snapshots per bar. */
  steps: number
  /** Bars folded ahead of the cursor. */
  lookahead: number
  /** Fetch when fewer than this many of the next `lookahead` bars are folded. */
  refillBelow: number
  /** Bars one request may cover. */
  maxBarsPerFill: number
}

/**
 * Snapshots to cut a bar into.
 *
 * Two ceilings, for different reasons. `MAX_REALTIME_STEPS` is memory. The resolution cap is
 * honesty: the M1 path has one row per minute, so more steps than minutes can only repeat a
 * state — and a repeated state is a paint that moves nothing. The tick path has no knowable row
 * count before the fetch, so it is left uncapped and the paint dedupe absorbs any sparseness.
 */
export function stepsForBar(
  barIntervalMs: number | null,
  tfSec: number,
  source: SubBarSource,
): number {
  const base =
    barIntervalMs == null || barIntervalMs <= 0
      ? DEFAULT_STEPS
      : Math.min(
          MAX_REALTIME_STEPS,
          Math.max(MIN_REALTIME_STEPS, Math.ceil(barIntervalMs / TARGET_STEP_MS)),
        )
  const resolutionCap =
    source === 'm1' ? Math.max(1, Math.floor(tfSec / SUB_BAR_SECONDS)) : Number.POSITIVE_INFINITY
  return Math.max(1, Math.min(base, resolutionCap))
}

/**
 * The whole geometry for one (source, timeframe, pacing) triple.
 *
 * The no-override branch is a CONSTANT 32, deliberately, rather than scaling with the play
 * speed: speed changes constantly in the Backtest tab, and a geometry that tracked it would
 * rebuild the ring — dropping all 16 prefetched bars — every time the user nudged the slider.
 * Detail and timeframe change rarely, which is what makes rebuilding on them affordable.
 */
export function formingBarPlan(input: {
  source: SubBarSource
  tfSec: number
  barIntervalMs: number | null
}): FormingBarPlan {
  const { source, tfSec, barIntervalMs } = input
  const realtime = barIntervalMs != null && barIntervalMs > 0
  const steps = stepsForBar(barIntervalMs, tfSec, source)

  if (realtime) {
    return {
      source,
      capacity: REALTIME_CAPACITY,
      steps,
      lookahead: REALTIME_LOOKAHEAD_BARS,
      refillBelow: REALTIME_LOOKAHEAD_BARS,
      maxBarsPerFill: REALTIME_MAX_BARS_PER_FILL,
    }
  }

  const lookahead = source === 'ticks' ? TICK_LOOKAHEAD_BARS : LOOKAHEAD_BARS
  return {
    source,
    capacity: DEFAULT_CAPACITY,
    steps,
    lookahead,
    refillBelow: Math.max(1, Math.floor(lookahead / 2)),
    maxBarsPerFill: lookahead,
  }
}

/** `capacity x steps` — the ring's cell count, for the memory assertions. */
export function planCells(plan: FormingBarPlan): number {
  return plan.capacity * plan.steps
}
